/**
 * entity: A Cesium.Entity
 * setMapObjects: A setter (e.g., React setState) that updates an array of map objects
 * viewerOrDataSources (optional): Cesium.Viewer OR an array/collection of dataSources to resolve the owner
 * Returns: true if the dataSource was resolved and the layer was marked as edited, otherwise false
 */
export function markEntityLayerEdited(entity, setMapObjects, viewerOrDataSources) {
  if (!entity || typeof entity !== "object") return false;

  // 1) Resolve the entity's DataSource (Cesium style)
  const ec = entity.entityCollection || entity._entityCollection;
  let dataSource = (ec && (ec.owner || ec._owner)) || null;

  // 2) If owner is not set, try to find the dataSource from viewer/dataSources
  if (!dataSource && viewerOrDataSources) {
    const dsCollection =
      // Cesium.Viewer
      viewerOrDataSources.dataSources ||
      // Cesium.DataSourceCollection or array-like
      viewerOrDataSources;

    const tryGetAll = (c) => {
      if (!c) return [];
      if (Array.isArray(c)) return c;
      if (typeof c.get === "function" && typeof c.length === "number") {
        // DataSourceCollection: .get(i)
        const out = [];
        for (let i = 0; i < c.length; i++) out.push(c.get(i));
        return out;
      }
      if (Array.isArray(c._dataSources)) return c._dataSources; // fallback for some wrappers
      return [];
    };

    const all = tryGetAll(dsCollection);

    for (const ds of all) {
      const entities = ds && ds.entities;
      if (!entities) continue;

      // Fast checks: getById / contains
      const byId = typeof entities.getById === "function" ? entities.getById(entity.id) : null;
      if (byId === entity) {
        dataSource = ds;
        break;
      }

      const contains = typeof entities.contains === "function" ? entities.contains(entity) : false;
      if (contains) {
        dataSource = ds;
        break;
      }
    }
  }

  if (!dataSource) return false;

  // 3) Get layerId from the resolved dataSource (your app-specific field)
  const layerId = dataSource.layerId ?? dataSource._layerId ?? dataSource.layerID ?? dataSource.layer_id ?? null;

  if (layerId == null) return false;

  // 4) Mark the matching map object as edited (id === layerId)
  setMapObjects((prev) => prev.map((obj) => (String(obj.id) === String(layerId) ? { ...obj, is_edited: true } : obj)));

  return true;
}
